import axios from 'axios';

// const { getAccountNumber } = require('./getAccountNumber.js');
const { handleOAuthFlow } = require('./oauth_flow');

const poweron_endpoint = process.env.REACT_APP_POWERON_ENDPOINT;
const poweron_key = process.env.REACT_APP_POWERON_KEY;
const poweron_api_key = process.env.REACT_APP_POWERON_API_KEY;
const POWERON_SPECFILE = 'DWD.SYMX.MBRREWARDS.PND';
const plugin_client_id = process.env.REACT_APP_PLUGIN_CLIENTID;
const plugin_client_secret = process.env.REACT_APP_PLUGIN_CLIENTSECRET;
const plugincard_client_id = process.env.REACT_APP_PLUGINCARD_CLIENTID;
const plugincard_client_secret = process.env.REACT_APP_PLUGINCARD_CLIENTSECRET;
const plugin_environment = process.env.REACT_APP_PLUGIN_ENVIRONMENT;
const plugin_base_uri = process.env.REACT_APP_PLUGIN_REDIRECTURI;

const executePowerOn = async (rgState, { charVars = [], numVars = [] } = {}) => {
  const body = {
    specfile: POWERON_SPECFILE,
    rgState,
    rgSession: 1,
    messageId: '1'
  };
  // ms-poweron rejects empty arrays -- omit them from the body entirely.
  if (charVars.length) body.charVars = charVars;
  if (numVars.length) body.numVars = numVars;

  const base = String(poweron_endpoint || '').replace(/\/+$/, '');

  const headers = { 'Content-Type': 'application/json' };
  // Gateway subscription key. Present when the call is routed through the MAX API gateway.
  if (poweron_key) headers['Ocp-Apim-Subscription-Key'] = poweron_key;
  // ms-poweron's own inbound key. Set it when calling the App Service directly
  // (staging); leave it unset in production, where the gateway injects the header.
  if (poweron_api_key) headers['x-api-key'] = poweron_api_key;

  try {
    const response = await axios.post(`${base}/poweron/execute`, body, { headers });
    return response.data.rgLines;
  } catch (error) {
    // ms-poweron returns { success, requestId, error, message } on every failure.
    const data = error.response?.data;
    if (data) {
      console.error(`PowerOn Error [${rgState}] ${data.error}: ${data.message || ''} (requestId: ${data.requestId})`);
    }
    throw error;
  }
};

// Utility function to get account number
const MEMBER_INFO_CACHE_KEY = 'mbrrewards_member_info';
const MEMBER_INFO_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

const getMemberInfo = async (client_id, client_secret, plugin_redirecturi) => {

  const timeout = 5000;

  // Get query parameters from URL
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');

  // If no auth code in URL, check sessionStorage cache before starting OAuth redirect
  if (!code) {
    try {
      const raw = sessionStorage.getItem(MEMBER_INFO_CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (Date.now() - cached.cached_at < MEMBER_INFO_CACHE_TTL) {
          return cached;
        }
      }
    } catch (_) {
      // Ignore cache read errors; fall through to OAuth flow
    }
  }

  const restartAuth = async () => {
    window.history.replaceState({}, '', window.location.pathname);
    sessionStorage.removeItem(MEMBER_INFO_CACHE_KEY);
    const redirectResult = await handleOAuthFlow(plugin_environment, client_id, client_secret, plugin_redirecturi);
    if (redirectResult && redirectResult.results === 'REDIRECT') {
      localStorage.setItem(`STATE_${redirectResult.state}`, redirectResult.code_verifier);
      window.location.href = redirectResult.redirect_url;
    }
    return null;
  };

  try {
    // Validate required parameters
    if (!client_id || !client_secret || !plugin_environment) {
        throw new Error('Missing required parameters');
    }

    // Create a promise that rejects after timeout
    const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error(`Request timed out after ${timeout}ms`));
        }, timeout);
    });

    const requestPromise = handleOAuthFlow(
      plugin_environment,
      client_id,
      client_secret,
      plugin_redirecturi
    );

    // Race between the timeout and the actual request
    const result = await Promise.race([requestPromise, timeoutPromise]);

    if (result) {
      if (result.results === 'OK'){
        // Cache successful auth result for subsequent page loads
        try {
          sessionStorage.setItem(MEMBER_INFO_CACHE_KEY, JSON.stringify({
            ...result,
            cached_at: Date.now()
          }));
        } catch (_) {
          // Ignore cache write errors; proceed normally
        }
        return result;
      } else if (result.results === 'ERROR'){
          // Clear stale state and silently re-authenticate instead of surfacing error
          return restartAuth();
      } else {
          // Store state and code_verifier in localStorage
          localStorage.setItem(`STATE_${result.state}`, result.code_verifier);
          // Redirect to the authorization URL
          window.location.href = result.redirect_url;
          return null;
      }

    }

  } catch(error) {
      // On any auth failure, silently restart OAuth instead of returning an error
      return restartAuth();
  }

};

export const fetchDashboardData = async () => {
  try {
    const member_info = await getMemberInfo(plugin_client_id, plugin_client_secret, plugin_base_uri);
    if (!member_info) return null; // Redirect is in progress
    if (member_info.results === 'ERROR'){
      throw new Error(member_info.error);
    }
    //console.log('Member Number: ',member_info.member_number)
    const parsed = await executePowerOn('START', {
      charVars: [
        { id: '1', value: member_info.member_number.padStart(10, "0") }
      ]
    });
    return parsed;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const fetchWidgetData = async () => {
  try {
    const member_info = await getMemberInfo(plugincard_client_id, plugincard_client_secret, `${plugin_base_uri}/widget`);
    //const acct_number = "0000561764"
    if (!member_info) return null; // Redirect is in progress
    if (member_info.results === 'ERROR'){
      throw new Error(member_info.error);
    }
    //console.log('Member Number: ',member_info.member_number)

    const parsed = await executePowerOn('THEBASICS', {
      charVars: [
        { id: '1', value: member_info.member_number.padStart(10, "0") }
      ]
    });
    return parsed;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const fetchRedeemData = async () => {
  try {
    const member_info = await getMemberInfo(plugin_client_id, plugin_client_secret, `${plugin_base_uri}/redeem`);
    //const acct_number = "0000561764"
    if (!member_info) return null; // Redirect is in progress
    if (member_info.results === 'ERROR'){
      throw new Error(member_info.error);
    }
    //console.log('Member Number: ',member_info.member_number)

    const parsed = await executePowerOn('THEBASICS', {
      charVars: [
        { id: '1', value: member_info.member_number.padStart(10, "0") }
      ]
    });
    return parsed;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const fetchRedeemCashData = async () => {
  try {
    const member_info = await getMemberInfo(plugin_client_id, plugin_client_secret, `${plugin_base_uri}/redeemcash`);
    if (!member_info) return null; // Redirect is in progress
    if (member_info.results === 'ERROR'){
      throw new Error(member_info.error);
    }
    //console.log('Member Number: ',member_info.member_number)

    const parsed = await executePowerOn('THEBASICS', {
      charVars: [
        { id: '1', value: member_info.member_number.padStart(10, "0") }
      ]
    });
    return parsed;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const fetchRedeemCashResultsData = async () => {
  try {
    const member_info = await getMemberInfo(plugin_client_id, plugin_client_secret, `${plugin_base_uri}/redeemcashresults`);
    if (!member_info) return null; // Redirect is in progress
    if (member_info.results === 'ERROR'){
      throw new Error(member_info.error);
    }
    //console.log('Redeem Cash for Member Number: ',member_info.member_number);

    const parsed = await executePowerOn('POSTREDEMPTION', {
      charVars: [
        { id: '1', value: member_info.member_number.padStart(10, "0") }
      ]
    });
    return parsed;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const fetchCharityListData = async () => {
  try {
    //const acct_number = "0000561764"
    const member_info = await getMemberInfo(plugin_client_id, plugin_client_secret, `${plugin_base_uri}/redeemdonate`);
    if (!member_info) return null; // Redirect is in progress
    if (member_info.results === 'ERROR'){
      throw new Error(member_info.error);
    }
    //console.log('Member Number: ',member_info.member_number)

    const parsed = await executePowerOn('CHARITYDASH', {
      charVars: [
        { id: '1', value: member_info.member_number.padStart(10, "0") }
      ]
    });
    return parsed;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const fetchGetPointsData = async () => {
  try {
    const member_info = await getMemberInfo(plugin_client_id, plugin_client_secret, `${plugin_base_uri}/getpoints`);
    if (!member_info) return null; // Redirect is in progress
    if (member_info.results === 'ERROR'){
      throw new Error(member_info.error);
    }

    const parsed = await executePowerOn('THEBASICS', {
      charVars: [
        { id: '1', value: member_info.member_number.padStart(10, "0") }
      ]
    });
    parsed.member_number = member_info.member_number;
    return parsed;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const submitCodeRedemption = async ({ code, member_number }) => {
  const debug = process.env.REACT_APP_LOG_LEVEL === 'debug';
  const paddedMember = member_number.padStart(10, "0");
  try {

    if (debug) {
      console.group('[Code Redemption] Request');
      console.log('Member number (raw):', member_number);
      console.log('Member number (padded):', paddedMember);
      console.log('Code:', code);
      console.log('Code length:', code.length);
      console.log('Code char codes:', [...code].map(c => c.charCodeAt(0)));
      console.groupEnd();
    }

    // Returns: { post_result: "SUCCESS", post_redeemed_pts, post_redeemed_worth }
    //       or { post_result: "ERROR", post_message }
    const parsed = await executePowerOn('CODEREDEEM', {
      charVars: [
        { id: '1', value: paddedMember },
        { id: '2', value: code }
      ]
    });

    if (debug) {
      console.group('[Code Redemption] Response');
      console.log('Parsed:', parsed);
      console.groupEnd();
    }

    if (!parsed) throw new Error('No response received from server.');
    return parsed;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const submitSalesforceCase = async ({ memberNumber, subject, case_type, description, file }) => {
  const debug = process.env.REACT_APP_LOG_LEVEL === 'debug';
  try {
    const sf_endpoint = process.env.REACT_APP_SF_CASE_ENDPOINT;
    const sf_gateway_key = process.env.REACT_APP_SF_GATEWAY_KEY;
    const url = `${sf_endpoint}/sfapi/case-create`;
    const requestBody = { memberNumber, subject, case_type, description: description ?? subject, ...(file && { file }) };
    const requestHeaders = {
      'x-backend-key': sf_gateway_key,
      'Content-Type': 'application/json'
    };

    if (debug) {
      console.group('[SF Case API] Request');
      console.log('URL:', url);
      console.log('Headers:', requestHeaders);
      console.log('Body:', requestBody);
      console.groupEnd();
    }

    const response = await axios.post(url, requestBody, { headers: requestHeaders });

    if (debug) {
      console.group('[SF Case API] Response');
      console.log('Status:', response.status);
      console.log('Data:', response.data);
      console.groupEnd();
    }

    if (response.status === 201) {
      return { success: true, caseId: response.data.caseId, message: response.data.message };
    }
    throw new Error(response.data.error || 'Unexpected response from Salesforce service');
  } catch (error) {
    if (error.response) {
      if (debug) {
        console.group('[SF Case API] Error Response');
        console.log('Status:', error.response.status);
        console.log('Data:', error.response.data);
        console.groupEnd();
      }
      throw new Error(error.response.data.error || `Request failed with status ${error.response.status}`);
    }
    console.error('[SF Case API] Error:', error);
    throw error;
  }
};

export const submitCharityDonation = async ({ charityId, points, member_number }) => {
    try {
        //const acct_number = "0000561764"
        //const member_info = await getMemberInfo(plugin_client_id, plugin_client_secret, 'https://rewards.plugins.mymax.com/redeemdonate');
        if (!member_number) return null; // Redirect is in progress
        //console.log('Donate from Member Number: ',member_number)

        const parsed = await executePowerOn('CHARITYDONATE', {
            charVars: [
                { id: '1', value: member_number.padStart(10, "0") },
                { id: '2', value: String(points) }
            ],
            numVars: [
                { id: '1', value: Number(charityId) }
            ]
        });
        return parsed;
    } catch (error) {
        console.error('API Error:', error);
        throw error;
    }
};

